import { shift, flip, useFloating } from "@floating-ui/react-dom"
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react"
import { ModalCompatiblePortal } from "components/common"
import { formatDate } from "components/ContentLibrary/utils"
import { RenderLabel } from "forms/RenderLabel"
import { FormControlProps } from "forms/types"
import { defaultInputClassNames } from "forms/utils"
import { ComponentProps, PropsWithChildren, useEffect, FC, ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { TbCalendar, TbChevronDown, TbX } from "react-icons/tb"
import { useCurrentUser } from "store/hooks"
import { classNames } from "utils"
import { Calendar, CalendarProps } from "./Calendar"

const DateSelector = ({
    children: Children,
    readOnly,
    modalRef,
    ...props
}: CalendarProps &
    FormControlProps<Date> & { children?: FC | ReactNode; modalRef?: React.RefObject<HTMLDivElement> }) => {
    const { refs, floatingStyles, update } = useFloating({
        placement: "bottom-start",
        middleware: [flip(), shift()]
    })

    useEffect(() => {
        if (!modalRef) return

        const modalElement = modalRef.current
        if (!modalElement) return

        const handleScroll = () => {
            update()
        }

        modalElement.addEventListener("scroll", handleScroll)
        return () => {
            modalElement.removeEventListener("scroll", handleScroll)
        }
    }, [update])

    return (
        <Popover className={props.classList?.wrapper} id={"calendar-panel-uuid"}>
            <RenderLabel {...props} />
            <PopoverButton
                ref={refs.setReference}
                className={props.classList?.input ?? defaultInputClassNames}
                disabled={readOnly}
            >
                {typeof Children === "function" ? <Children /> : Children}
            </PopoverButton>
            <ModalCompatiblePortal>
                <PopoverPanel
                    ref={refs.setFloating}
                    className="w-80 bg-white shadow-md border border-border my-2 p-4 rounded-md z-[110000000]"
                    style={floatingStyles}
                >
                    {({ close }) => (
                        <Calendar
                            {...props}
                            setValue={(value) => {
                                props.setValue(value)
                                close()
                            }}
                        />
                    )}
                </PopoverPanel>
            </ModalCompatiblePortal>
        </Popover>
    )
}

export const DateSelectorWithIcons = ({
    allowClear = true,
    modalRef,
    ...props
}: Omit<ComponentProps<typeof DateSelector>, "children"> & {
    allowClear?: boolean
    modalRef?: React.RefObject<HTMLDivElement>
}) => {
    const { i18n } = useTranslation()
    const currentUser = useCurrentUser()
    return (
        <DateSelector modalRef={modalRef} {...props}>
            {({ open }: { open: boolean }) => (
                <>
                    <div className="flex gap-3 items-center">
                        <TbCalendar className="w-6 h-6" />
                        <span>
                            {props.value ? formatDate(props.value, currentUser.preferences, i18n) : props.placeholder}
                        </span>
                    </div>
                    <div className="flex gap-2">
                        {allowClear && props.value && (
                            <TbX
                                className="text-xs hover:scale-125 transition-transform self-center"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    e.preventDefault()
                                    props.setValue(undefined)
                                }}
                            />
                        )}
                        <TbChevronDown className={classNames("text-xl transition self-center", open && "rotate-180")} />
                    </div>
                </>
            )}
        </DateSelector>
    )
}

export default DateSelector
